import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Box, X } from 'lucide-react';
import { useSummaryDialog } from './lib/useSummaryDialog';

const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5008';
const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const percent = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const palette = ['#2563eb', '#f97316', '#22c55e', '#a855f7', '#ec4899', '#06b6d4'];
const familyColors = {
  ADUELAS: palette[0], 'TUBOS DE CONCRETO': palette[1], PAVIMENTOS: palette[2],
  ESTRUTURAS: palette[3], 'PECAS DE PRATELEIRA': palette[4], 'PECAS ESPECIAIS': palette[5],
};

function normalizeRows(json) {
  if (!Array.isArray(json)) throw new Error('Resposta inválida ao consultar o estoque por família.');
  return json.map(row => {
    const venda = Number(row.valorEstoquePrecoVenda ?? 0);
    const minimo = Number(row.valorEstoquePrecoMinimo ?? 0);
    if (!Number.isFinite(venda) || !Number.isFinite(minimo)) throw new Error('Valor inválido no estoque por família.');
    return {
      familia: String(row.familia || 'Família não informada').trim(),
      valorEstoquePrecoVenda: venda,
      valorEstoquePrecoMinimo: minimo,
    };
  });
}

function FamilyDistribution({ title, field, families }) {
  const amounts = families.map(family => ({ ...family, cents: Math.round(family[field] * 100) }));
  const totalCents = amounts.reduce((sum, family) => sum + family.cents, 0);
  const hasNegative = amounts.some(family => family.cents < 0);
  const rows = amounts.map(family => ({ ...family, share: totalCents !== 0 ? family.cents / totalCents * 100 : 0 }));

  return (
    <section aria-label={title} className="min-w-0 py-6 first:pt-0 last:pb-0 sm:py-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <h3 className="flex min-w-0 items-center gap-3 text-sm font-bold uppercase tracking-wide text-purple-400 sm:text-base lg:text-lg">
          <Box aria-hidden="true" className="h-6 w-6 shrink-0 sm:h-8 sm:w-8" />{title}
        </h3>
        <p className="break-words font-mono text-2xl font-bold italic tracking-tight text-green-500 sm:text-3xl lg:text-4xl">{brl.format(totalCents / 100)}</p>
      </div>

      {!hasNegative && totalCents > 0 && (
        <div className="mt-6 flex h-5 w-full overflow-hidden rounded-full bg-gray-800 sm:h-6" role="img" aria-label={'Distribuição de ' + title.toLowerCase()}>
          {rows.filter(family => family.cents > 0).map(family => <div key={family.familia} className="h-full shrink-0" style={{ width: family.share + '%', backgroundColor: family.color }} title={family.familia + ': ' + percent.format(family.share) + '% · ' + brl.format(family.cents / 100)} />)}
        </div>
      )}
      {hasNegative && <p className="mt-5 text-xs text-gray-400">A distribuição em barra não se aplica a saldos negativos; consulte os valores abaixo.</p>}
      {!hasNegative && totalCents === 0 && <p className="mt-5 text-sm text-gray-400">Sem valor de estoque nesta modalidade.</p>}

      <dl className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 min-[360px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {rows.map(family => (
          <div key={family.familia} role="group" aria-label={family.familia} className="flex min-w-0 items-start gap-3">
            <span aria-hidden="true" className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full sm:h-4 sm:w-4" style={{ backgroundColor: family.color }} />
            <div className="min-w-0">
              <dt className="break-words text-[11px] font-medium uppercase leading-5 tracking-wide text-gray-300 sm:text-xs">{family.familia}</dt>
              <dd className="mt-1 font-mono text-xl font-bold" style={{ color: family.color }}>{percent.format(family.share)}%</dd>
              <dd className="mt-1 break-words font-mono text-xs leading-5 text-gray-300 sm:text-sm">{brl.format(family.cents / 100)}</dd>
            </div>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function EstoqueTotal({ onClose, empresa = '' }) {
  const { titleId, dialogRef, closeRef, onKeyDown } = useSummaryDialog(onClose);
  const [state, setState] = useState({ rows: [], loading: true, error: '' });
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setState({ rows: [], loading: true, error: '' });
      try {
        const token = localStorage.getItem('@user_token');
        if (!token) throw new Error('Sua sessão expirou. Faça login novamente.');
        const query = empresa ? '?' + new URLSearchParams({ empresa }) : '';
        const response = await fetch(baseUrl.replace(/\/$/, '') + '/api/dashboard/Estoque/familias' + query, { headers: { Authorization: 'Bearer ' + token }, signal: controller.signal });
        const messages = { 401: 'Sua sessão expirou. Faça login novamente.', 403: 'Você não tem permissão para consultar o estoque por família.', 429: 'Muitas consultas. Aguarde e tente novamente.' };
        if (!response.ok) throw new Error(messages[response.status] || 'Não foi possível carregar o estoque por família.');
        const rows = normalizeRows(response.status === 204 ? [] : await response.json());
        if (!controller.signal.aborted) setState({ rows, loading: false, error: '' });
      } catch (error) {
        if (!controller.signal.aborted) setState({ rows: [], loading: false, error: error.message });
      }
    }
    load();
    return () => controller.abort();
  }, [empresa]);

  const families = useMemo(() => [...state.rows]
    .sort((a, b) => b.valorEstoquePrecoVenda - a.valorEstoquePrecoVenda || a.familia.localeCompare(b.familia, 'pt-BR'))
    .map((family, index) => {
      const key = family.familia.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      return { ...family, color: familyColors[key] || palette[index % palette.length] };
    }), [state.rows]);

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 p-2 backdrop-blur-sm sm:p-4">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown} className="flex max-h-[calc(100dvh-1rem)] min-h-0 w-full max-w-7xl flex-col overflow-hidden rounded-xl border border-gray-800 bg-[#10161d] shadow-2xl sm:max-h-[calc(100dvh-2rem)]">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-800 bg-[#161b22] p-3 sm:px-6 sm:py-3">
          <h2 id={titleId} className="flex min-w-0 items-center gap-2 text-xs font-bold uppercase tracking-wide text-purple-400"><Box aria-hidden="true" size={18} className="shrink-0" />Detalhamento de estoque</h2>
          <button type="button" ref={closeRef} onClick={onClose} aria-label="Fechar detalhes do estoque" className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-gray-700 text-gray-200 hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-400"><X aria-hidden="true" size={20} /></button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 lg:p-8" aria-live="polite">
          {state.loading && <p role="status" className="animate-pulse py-8 text-sm text-gray-400">Carregando estoque por família...</p>}
          {!state.loading && state.error && <p role="alert" className="flex items-start gap-2 py-6 text-sm text-red-300"><AlertTriangle aria-hidden="true" size={18} className="shrink-0" />{state.error}</p>}
          {!state.loading && !state.error && families.length === 0 && <p className="py-8 text-sm text-gray-400">Nenhuma família de estoque encontrada para esta seleção.</p>}
          {!state.loading && !state.error && families.length > 0 && <div className="divide-y divide-gray-800">
            <FamilyDistribution title="Estoque preço de tabela" field="valorEstoquePrecoVenda" families={families} />
            <FamilyDistribution title="Estoque preço mínimo" field="valorEstoquePrecoMinimo" families={families} />
          </div>}
        </div>
        <footer className="shrink-0 border-t border-gray-800 p-3 text-right sm:px-6 sm:py-3">
          <button type="button" onClick={onClose} className="min-h-11 w-full rounded bg-gray-800 px-8 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-400 sm:w-auto">Fechar Janela</button>
        </footer>
      </section>
    </div>
  );
}
